import React from "react";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import {
  CleaningConfigurationLevel,
  getCleaningToggleOptionText,
  getFrontLightsOptionText,
} from "../../../../interfaces/robot/cleaning-configurations/global-cleaning-configuration";
import FrontLightsMode from "../../../../interfaces/robot/cleaning-configurations/front-lights-mode";
import OptionSwitchInput, { PlaceholderOptions } from "../../../Inputs/OptionSwitchInput";
import { CustomTuneFiltersIcon, InheritIcon } from "../../../../utils/icons/icons";
import Divider from "@mui/material/Divider";
import { MapRulesConfiguration } from "../../../../interfaces/robot/cleaning-configurations/map-rules-configuration";
import {
  BEEPER_DEFAULT_VALUE,
  FRONT_LIGHTS_DEFAULT_VALUE,
  WARNING_LIGHT_DEFAULT_VALUE,
} from "../../../Room/ZonePlanner/SelectedControls/SelectedZoneFormRuleZoneDetails";
import RobotVersionControlledWrapper from "../../../misc/RobotVersionControlledWrapper";

const VisibilityConfiguration: React.FC<{
  configurationLevel: CleaningConfigurationLevel;
  globalMapRulesConfiguration?: MapRulesConfiguration;
  roomMapRulesConfiguration?: MapRulesConfiguration;
  zoneMapRulesConfiguration?: MapRulesConfiguration;
  onMapRulesConfigurationChange: (payload: Partial<MapRulesConfiguration>) => void;
}> = ({
  configurationLevel,
  globalMapRulesConfiguration,
  roomMapRulesConfiguration,
  zoneMapRulesConfiguration,
  onMapRulesConfigurationChange,
}) => {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <Stack
        spacing={2}
        divider={configurationLevel === CleaningConfigurationLevel.Zone ? undefined : <Divider flexItem />}
      >
        <RobotVersionControlledWrapper
          minVersion="2026.1.0"
          forceAllow={configurationLevel !== CleaningConfigurationLevel.Room}
        >
          <OptionSwitchInput
            label={t("cleaning-configuration-front-lights-mode")}
            options={Object.values(FrontLightsMode).map((m) => ({
              label: t(`cleaning-configuration-front-lights-mode-${m}`),
              value: m,
            }))}
            value={
              zoneMapRulesConfiguration?.frontLightsMode != null
                ? zoneMapRulesConfiguration.frontLightsMode
                : roomMapRulesConfiguration?.frontLightsMode != null
                  ? roomMapRulesConfiguration.frontLightsMode
                  : (globalMapRulesConfiguration?.frontLightsMode ?? FRONT_LIGHTS_DEFAULT_VALUE)
            }
            loading={
              (configurationLevel === CleaningConfigurationLevel.Zone && zoneMapRulesConfiguration == null) ||
              (configurationLevel === CleaningConfigurationLevel.Room && roomMapRulesConfiguration == null) ||
              globalMapRulesConfiguration == null
            }
            onChange={(v: string) => onMapRulesConfigurationChange({ frontLightsMode: v as FrontLightsMode })}
            subButton={
              zoneMapRulesConfiguration != null
                ? {
                    label:
                      zoneMapRulesConfiguration.frontLightsMode == null
                        ? t("button-override-default-configs")
                        : getFrontLightsOptionText(
                            null,
                            configurationLevel,
                            roomMapRulesConfiguration?.frontLightsMode,
                            globalMapRulesConfiguration?.frontLightsMode ?? undefined,
                          ),
                    startIcon:
                      zoneMapRulesConfiguration.frontLightsMode == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
                    onClick: () =>
                      onMapRulesConfigurationChange({
                        frontLightsMode:
                          zoneMapRulesConfiguration.frontLightsMode == null
                            ? (roomMapRulesConfiguration?.frontLightsMode ??
                              globalMapRulesConfiguration?.frontLightsMode ??
                              FRONT_LIGHTS_DEFAULT_VALUE)
                            : null,
                      }),
                  }
                : roomMapRulesConfiguration != null
                  ? {
                      label:
                        roomMapRulesConfiguration.frontLightsMode == null
                          ? t("button-override-default-configs")
                          : getFrontLightsOptionText(
                              null,
                              configurationLevel,
                              roomMapRulesConfiguration?.frontLightsMode,
                              globalMapRulesConfiguration?.frontLightsMode ?? undefined,
                            ),
                      startIcon:
                        roomMapRulesConfiguration.frontLightsMode == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
                      onClick: () =>
                        onMapRulesConfigurationChange({
                          frontLightsMode:
                            roomMapRulesConfiguration.frontLightsMode == null
                              ? (globalMapRulesConfiguration?.frontLightsMode ?? FRONT_LIGHTS_DEFAULT_VALUE)
                              : null,
                        }),
                    }
                  : undefined
            }
            buttonOverrideText={
              configurationLevel === CleaningConfigurationLevel.Zone &&
              zoneMapRulesConfiguration?.frontLightsMode == null
                ? getFrontLightsOptionText(
                    null,
                    configurationLevel,
                    roomMapRulesConfiguration?.frontLightsMode,
                    globalMapRulesConfiguration?.frontLightsMode ?? undefined,
                  )
                : configurationLevel === CleaningConfigurationLevel.Room &&
                    roomMapRulesConfiguration?.frontLightsMode == null
                  ? getFrontLightsOptionText(
                      null,
                      configurationLevel,
                      roomMapRulesConfiguration?.frontLightsMode,
                      globalMapRulesConfiguration?.frontLightsMode ?? undefined,
                    )
                  : undefined
            }
          />
        </RobotVersionControlledWrapper>

        <OptionSwitchInput
          label={t("cleaning-configuration-beeper")}
          options={[
            {
              label: t("cleaning-configuration-switch-on"),
              value: PlaceholderOptions.True,
            },
            {
              label: t("cleaning-configuration-switch-off"),
              value: PlaceholderOptions.False,
            },
          ]}
          value={
            zoneMapRulesConfiguration?.beeper != null
              ? zoneMapRulesConfiguration.beeper
                ? PlaceholderOptions.True
                : PlaceholderOptions.False
              : roomMapRulesConfiguration?.beeper != null
                ? roomMapRulesConfiguration.beeper
                  ? PlaceholderOptions.True
                  : PlaceholderOptions.False
                : (globalMapRulesConfiguration?.beeper ?? BEEPER_DEFAULT_VALUE)
                  ? PlaceholderOptions.True
                  : PlaceholderOptions.False
          }
          loading={
            (configurationLevel === CleaningConfigurationLevel.Zone && zoneMapRulesConfiguration == null) ||
            (configurationLevel === CleaningConfigurationLevel.Room && roomMapRulesConfiguration == null) ||
            globalMapRulesConfiguration == null
          }
          onChange={(v: string) => onMapRulesConfigurationChange({ beeper: v === PlaceholderOptions.True })}
          subButton={
            zoneMapRulesConfiguration != null
              ? {
                  label:
                    zoneMapRulesConfiguration.beeper == null
                      ? t("button-override-default-configs")
                      : getCleaningToggleOptionText(
                          null,
                          configurationLevel,
                          roomMapRulesConfiguration?.beeper,
                          globalMapRulesConfiguration?.beeper ?? undefined,
                        ),
                  startIcon: zoneMapRulesConfiguration.beeper == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
                  onClick: () =>
                    onMapRulesConfigurationChange({
                      beeper:
                        zoneMapRulesConfiguration.beeper == null
                          ? (roomMapRulesConfiguration?.beeper ??
                            globalMapRulesConfiguration?.beeper ??
                            BEEPER_DEFAULT_VALUE)
                          : null,
                    }),
                }
              : roomMapRulesConfiguration != null
                ? {
                    label:
                      roomMapRulesConfiguration.beeper == null
                        ? t("button-override-default-configs")
                        : getCleaningToggleOptionText(
                            null,
                            configurationLevel,
                            roomMapRulesConfiguration?.beeper,
                            globalMapRulesConfiguration?.beeper ?? undefined,
                          ),
                    startIcon: roomMapRulesConfiguration.beeper == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
                    onClick: () =>
                      onMapRulesConfigurationChange({
                        beeper:
                          roomMapRulesConfiguration.beeper == null
                            ? (globalMapRulesConfiguration?.beeper ?? BEEPER_DEFAULT_VALUE)
                            : null,
                      }),
                  }
                : undefined
          }
          buttonOverrideText={
            configurationLevel === CleaningConfigurationLevel.Zone && zoneMapRulesConfiguration?.beeper == null
              ? getCleaningToggleOptionText(
                  null,
                  configurationLevel,
                  roomMapRulesConfiguration?.beeper,
                  globalMapRulesConfiguration?.beeper ?? undefined,
                )
              : configurationLevel === CleaningConfigurationLevel.Room && roomMapRulesConfiguration?.beeper == null
                ? getCleaningToggleOptionText(
                    null,
                    configurationLevel,
                    roomMapRulesConfiguration?.beeper,
                    globalMapRulesConfiguration?.beeper ?? undefined,
                  )
                : undefined
          }
        />

        <OptionSwitchInput
          label={t("cleaning-configuration-warning-light")}
          options={[
            {
              label: t("cleaning-configuration-switch-on"),
              value: PlaceholderOptions.True,
            },
            {
              label: t("cleaning-configuration-switch-off"),
              value: PlaceholderOptions.False,
            },
          ]}
          value={
            zoneMapRulesConfiguration?.warningLight != null
              ? zoneMapRulesConfiguration.warningLight
                ? PlaceholderOptions.True
                : PlaceholderOptions.False
              : roomMapRulesConfiguration?.warningLight != null
                ? roomMapRulesConfiguration.warningLight
                  ? PlaceholderOptions.True
                  : PlaceholderOptions.False
                : (globalMapRulesConfiguration?.warningLight ?? WARNING_LIGHT_DEFAULT_VALUE)
                  ? PlaceholderOptions.True
                  : PlaceholderOptions.False
          }
          loading={
            (configurationLevel === CleaningConfigurationLevel.Zone && zoneMapRulesConfiguration == null) ||
            (configurationLevel === CleaningConfigurationLevel.Room && roomMapRulesConfiguration == null) ||
            globalMapRulesConfiguration == null
          }
          onChange={(v: string) => onMapRulesConfigurationChange({ warningLight: v === PlaceholderOptions.True })}
          subButton={
            zoneMapRulesConfiguration != null
              ? {
                  label:
                    zoneMapRulesConfiguration.warningLight == null
                      ? t("button-override-default-configs")
                      : getCleaningToggleOptionText(
                          null,
                          configurationLevel,
                          roomMapRulesConfiguration?.warningLight,
                          globalMapRulesConfiguration?.warningLight ?? undefined,
                        ),
                  startIcon:
                    zoneMapRulesConfiguration.warningLight == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
                  onClick: () =>
                    onMapRulesConfigurationChange({
                      warningLight:
                        zoneMapRulesConfiguration.warningLight == null
                          ? (roomMapRulesConfiguration?.warningLight ??
                            globalMapRulesConfiguration?.warningLight ??
                            WARNING_LIGHT_DEFAULT_VALUE)
                          : null,
                    }),
                }
              : roomMapRulesConfiguration != null
                ? {
                    label:
                      roomMapRulesConfiguration.warningLight == null
                        ? t("button-override-default-configs")
                        : getCleaningToggleOptionText(
                            null,
                            configurationLevel,
                            roomMapRulesConfiguration?.warningLight,
                            globalMapRulesConfiguration?.warningLight ?? undefined,
                          ),
                    startIcon:
                      roomMapRulesConfiguration.warningLight == null ? <CustomTuneFiltersIcon /> : <InheritIcon />,
                    onClick: () =>
                      onMapRulesConfigurationChange({
                        warningLight:
                          roomMapRulesConfiguration.warningLight == null
                            ? (globalMapRulesConfiguration?.warningLight ?? WARNING_LIGHT_DEFAULT_VALUE)
                            : null,
                      }),
                  }
                : undefined
          }
          buttonOverrideText={
            configurationLevel === CleaningConfigurationLevel.Zone && zoneMapRulesConfiguration?.warningLight == null
              ? getCleaningToggleOptionText(
                  null,
                  configurationLevel,
                  roomMapRulesConfiguration?.warningLight,
                  globalMapRulesConfiguration?.warningLight ?? undefined,
                )
              : configurationLevel === CleaningConfigurationLevel.Room &&
                  roomMapRulesConfiguration?.warningLight == null
                ? getCleaningToggleOptionText(
                    null,
                    configurationLevel,
                    roomMapRulesConfiguration?.warningLight,
                    globalMapRulesConfiguration?.warningLight ?? undefined,
                  )
                : undefined
          }
        />
      </Stack>
    </React.Fragment>
  );
};

export default VisibilityConfiguration;
